/**
 * Tutor create form.
 * POSTs the profile then optionally enrolls the new user in the selected information session.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCreateTutorRemoteProfile } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { useFormValidation } from '@/hooks/useFormValidation';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import { REMOTE_SUBJECT_OPTIONS } from '@/components/tutors/tutor-configs';
import { tutorRemoteCreateValidationSchema } from '@/utils/validation-schemas-utils';
import {
  areContactsIdentical,
  isPrimaryContactFormValid,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { getLevels } from '@/services/levels.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import {
  buildCreateInitialData,
  buildCreateTutorDto,
  applyCriminalRecordOnCreate,
  setNestedPath,
  defaultUpload,
  mergePendingUploads,
} from './tutor-form-utils';
import { addSessionParticipant } from '@/services/remote/sessions.service';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { TutorPersonalAccountSection } from './tutor-form-builder/TutorPersonalAccountSection';
import { TutorOfficialDocumentsSection } from './tutor-form-builder/TutorOfficialDocumentsSection';
import { TutorSupportSection } from './tutor-form-builder/TutorSupportSection';
import { TutorInformationMeetingSection } from './tutor-form-builder/TutorInformationMeetingSection';
import { TutorCertificationSection } from './tutor-form-builder/TutorCertificationSection';
import { TutorContractSection } from './tutor-form-builder/TutorContractSection';

/** Close / success callbacks for the remote tutor create panel. */
interface CreateTutorViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Tutor create form. */
export function CreateTutorView({ onClose, onSuccess }: Readonly<CreateTutorViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(tutorRemoteCreateValidationSchema);

  const { data: levels = [] } = useQuery({
    queryKey: ['levels', 'all'],
    queryFn: async () => (await getLevels({ pageSize: 10 })).data,
  });

  const { data: programsResponse } = useProgramsForMode();
  const programs = programsResponse?.data ?? [];

  const subjectOptions = REMOTE_SUBJECT_OPTIONS;

  const levelOptions = React.useMemo(
    () => levels.map((l: any) => ({ value: l.id, label: l.label })),
    [levels],
  );

  const [formData, setFormData] = React.useState<Record<string, any>>(() =>
    buildCreateInitialData(defaultCountry),
  );

  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: formData.contact || '',
    type: resolveContactCheckType(formData.contactType ?? formData._contactType),
    enabled:
      (formData.contactType ?? formData._contactType) !== 'unknown' &&
      (formData.contact || '').trim().length > 0,
  });

  const secondaryContactType =
    (formData.contactType ?? formData._contactType) === 'email' ? 'phone' : 'email';
  const { exists: secondaryContactExists, isChecking: isSecondaryChecking } = useContactExistsCheck(
    {
      value: formData.secondaryContact || '',
      type: secondaryContactType,
      enabled:
        (formData.contactType ?? formData._contactType) !== 'unknown' &&
        (formData.secondaryContact || '').trim().length > 0,
    },
  );

  const contactsAreIdentical = areContactsIdentical(
    formData.contact || '',
    formData.secondaryContact || '',
  );

  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  React.useEffect(() => {
    validation.validateInitialFormData(formData);
  }, []);

  const createMutation = useCreateTutorRemoteProfile();

  /** Set one field (supports dotted paths). Changing contact type drops the secondary contact. */
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => {
      const next = key.includes('.') ? setNestedPath(prev, key, value) : { ...prev, [key]: value };
      if (
        key === 'contactType' &&
        prev.contactType &&
        prev.contactType !== 'unknown' &&
        value !== prev.contactType
      ) {
        next.secondaryContact = '';
        next.secondaryContactCountryCode = defaultCountry;
      }
      const updatedData = { ...next, _contactType: next.contactType };
      validation.validateField(key, value, updatedData);
      return next;
    });
    validation.markAsTouched(key);
    validation.markAsDirty(key);
  };

  /** Patch several fields at once (supports dotted paths). Validates after the state flush. */
  const handleBatchChange = (patch: Record<string, any>) => {
    setFormData((prev) => {
      let next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (key.includes('.')) {
          next = setNestedPath(next, key, value);
        } else {
          next[key] = value;
        }
      });
      formDataRef.current = next;
      return next;
    });
    setTimeout(() => {
      const updatedData = { ...formDataRef.current, _contactType: formDataRef.current.contactType };
      Object.entries(patch).forEach(([k, v]) => {
        validation.validateField(k, v, updatedData);
        validation.markAsTouched(k);
        validation.markAsDirty(k);
      });
    }, 0);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Store the file for submit upload and write a pending link so required docs validate. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (patch) handleBatchChange(patch);
  };

  /** Validate, upload pending files, POST profile, then enroll in the selected information session if any. */
  const handleSubmit = async () => {
    const isValid = validation.validateForm({
      ...formData,
      _contactType: formData.contactType,
      _contactExists: contactExists,
    });
    if (!isValid) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, defaultUpload);
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    const dto = buildCreateTutorDto(enrichedData, defaultCountry);
    createMutation.mutate(
      { dto, companyId },
      {
        onSuccess: async (data) => {
          const newUserId = (data as any)?.user?.id as string | undefined;
          if (formData.sessionId && newUserId) {
            try {
              await addSessionParticipant(formData.sessionId, {
                userId: newUserId,
                role: 'volunteer',
              });
            } catch (err) {
              console.warn('[CreateTutorView] Could not enroll new user in session:', err);
            }
          }
          await applyCriminalRecordOnCreate(
            newUserId,
            formData.criminalRecord?.markValidated === true,
          );
          toast.success('Tuteur créé avec succès');
          onSuccess?.();
        },
        onError: (err: unknown) => {
          toast.error(getErrorMessage(err));
        },
      },
    );
    setIsUploading(false);
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un tuteur"
      subtitle="Renseignez les informations du tuteur"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isUploading || createMutation.isPending}
      isValid={isPrimaryContactFormValid({
        primaryExists: contactExists,
        secondaryExists: secondaryContactExists,
        isPrimaryChecking,
        isSecondaryChecking,
        identical: contactsAreIdentical,
        formValid: validation.isFormValid(),
      })}
    >
      <TutorPersonalAccountSection
        formData={formData}
        onChange={handleChange}
        onBlur={handleBlur}
        validation={validation}
        mode="create"
        defaultCountry={defaultCountry}
        contactExists={contactExists}
        secondaryContactExists={secondaryContactExists}
        contactsAreIdentical={contactsAreIdentical}
      />
      <TutorOfficialDocumentsSection
        formData={formData}
        onChange={handleChange}
        tutorUserId=""
        onFileSelect={handleFileChange}
      />
      <TutorSupportSection
        formData={formData}
        onChange={handleChange}
        programs={programs}
        levelOptions={levelOptions}
        subjectOptions={subjectOptions}
        mode="create"
      />
      <TutorInformationMeetingSection
        formData={formData}
        onChange={handleChange}
        userId=""
        companyId={companyId}
        mode="create"
        user={user}
      />
      <TutorCertificationSection
        formData={formData}
        onChange={handleChange}
        onFileSelect={handleFileChange}
      />
      <TutorContractSection
        formData={formData}
        onChange={handleChange}
        onBatchChange={handleBatchChange}
        onFileSelect={handleFileChange}
      />
    </CollapseReplaceForm>
  );
}
